import { TravelerKindEnum } from '@koob/enums';
import { BookingContext } from 'contexts';
import { uniqueId } from 'lodash';
import {
  blankToUndefined,
  NATIONALITY_OPTIONS,
  splitFolderTravelers,
} from '@koob/utils';
import { useCallback, useState, useMemo } from 'react';

const filtersValidations = {
  rooms: (composition = []) => {
    const total = composition?.reduce((acc, curr) => {
      return acc + (curr?.adults ?? 0) + (curr?.children ?? 0);
    }, 0);
    return total !== 0;
  },
  location: (value) => !!value,
  startAndEndDates: (start, end) => {
    return !!start && !!end;
  },
};

export const getTravelerComposition = ({
  compositions,
  customerFile,
  nationalities,
  typeFieldName = 'kind',
  checkInDate,
}) => {
  const referenceDate = checkInDate ? new Date(checkInDate) : new Date();
  const travelerRooms = (compositions || [])?.reduce((acc, curr) => {
    const peoples = [];
    for (let i = 0; i < curr.adults; i++) {
      const id = uniqueId();
      const people = { id };
      people[typeFieldName] = TravelerKindEnum.ADULT;
      people.indexOfKind = i;
      peoples.push(people);
    }
    for (let j = 0; j < curr.children; j++) {
      const id = uniqueId();
      const people = { id };
      people[typeFieldName] = TravelerKindEnum.CHILD;
      people.indexOfKind = j;
      peoples.push(people);
    }
    acc.push({ ...curr, travelers: peoples });
    return acc;
  }, []);

  if (customerFile) {
    let adultIndex = 0;
    let childIndex = 0;
    // KD-3546: seats are matched on the folder's own kind/position, not on an
    // age recomputed from the birthdate — a placeholder has no birthdate yet and
    // would otherwise be skipped, leaving booking to create a duplicate folder
    // traveler instead of completing it.
    const { adults, children: childs } = splitFolderTravelers(
      customerFile?.travelers,
      referenceDate,
      customerFile?.mainTravelerId,
    );
    travelerRooms.forEach((room) => {
      room.travelers.forEach((traveler, index) => {
        let person = undefined;
        if (traveler[typeFieldName] === TravelerKindEnum.ADULT) {
          if (adultIndex < adults?.length) {
            person = adults[adultIndex];
            adultIndex++;
          }
        } else {
          if (childIndex < childs?.length) {
            person = childs[childIndex];
            childIndex++;
          }
        }
        if (person) {
          // A placeholder carries nulls, which the pax form's Yup schemas
          // reject on sight: an unfilled field stays undefined.
          traveler.id = person.id;
          traveler.gender = blankToUndefined(person.gender);
          traveler.firstName = blankToUndefined(person.firstName);
          traveler.lastName = blankToUndefined(person.lastName);
          traveler.birthdate = blankToUndefined(person.birthdate);
          traveler.nationality = nationalities?.find(
            (nationality) =>
              nationality?.value ===
              (index === 0
                ? room?.firstAdultNationality?.value || person.nationality
                : person.nationality),
          );
          traveler.passportNumber = blankToUndefined(person.passportNumber);
          traveler.expirationDate = blankToUndefined(
            person.passportExpirationDate,
          );
          traveler.ageIsExact = blankToUndefined(person.ageIsExact);
        } else {
          traveler.nationality =
            index === 0 ? room?.firstAdultNationality : undefined;
        }
      });
    });
  } else {
    travelerRooms.forEach((room) => {
      room.travelers.forEach((traveler, index) => {
        if (index === 0) {
          traveler.nationality = room?.firstAdultNationality;
        } else {
          traveler.nationality = null;
        }
      });
    });
  }
  return travelerRooms;
};

const BookingProvider = ({ children }) => {
  const [filters, setFilters] = useState({});
  const [customerFile, setCustomerFile] = useState(undefined);
  const [bookingSource, setBookingSource] = useState(undefined);
  const [bookingId, setBookingId] = useState(undefined);
  const [replaceBookingId, setReplaceBookingId] = useState(undefined);
  const [guests, setGuests] = useState([]);
  const [bedIndexes, setBedIndexes] = useState([]);
  const [requestId, setRequestId] = useState();
  const [bookingData, setBookingData] = useState(null);

  const filtersValidity = useMemo(() => {
    // KD-3523: booking without a client folder is rejected by the API,
    // so a folder is now required before showing bookable results.
    if (!customerFile) {
      return false;
    }
    if (!filtersValidations.rooms(filters?.rooms)) {
      return false;
    }
    if (!filtersValidations.location(filters?.location)) {
      return false;
    }
    if (
      !filtersValidations.startAndEndDates(filters?.startAt, filters?.endAt)
    ) {
      return false;
    }
    return true;
  }, [filters, customerFile]);

  const resetValues = useCallback(() => {
    setFilters({});
  }, []);

  const value = {
    filters,
    setFilters,
    filtersValidity,
    customerFile,
    setCustomerFile,
    resetValues,
    bookingSource,
    setBookingSource,
    bookingId,
    setBookingId,
    replaceBookingId,
    setReplaceBookingId,
    bedIndexes,
    setBedIndexes,
    guests,
    setGuests,
    nationalities: NATIONALITY_OPTIONS,
    setRequestId,
    requestId,
    bookingData,
    setBookingData,
  };

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
};

export default BookingProvider;
